import { DEFAULT_EMAIL_PREFS, type EmailPrefs } from './types.js';

/** Wie E-Mails ankommen: einzeln (nach kurzer Wartezeit), gebündelt oder einmal täglich */
export type EmailMode = 'instant' | 'bundle' | 'daily';
export const EMAIL_MODE_LABEL: Record<EmailMode, string> = {
  instant: 'Sofort (einzeln)',
  bundle: 'Gebündelt (höchstens alle 15 Minuten)',
  daily: 'Einmal täglich (morgens)',
};

export interface QuietHours {
  enabled: boolean;
  /** „HH:MM“ */
  from: string;
  to: string;
  /** Wochenende komplett ruhig */
  weekend: boolean;
}

export interface NotifyPrefs {
  /** E-Mail je Art */
  email: EmailPrefs;
  /** Push je Art */
  push: EmailPrefs;
  emailMode: EmailMode;
  quiet: QuietHours;
  /** Zeitzone der Person (vom Browser), sonst die der Instanz */
  timeZone: string | null;
}

export const DEFAULT_NOTIFY_PREFS: NotifyPrefs = {
  email: DEFAULT_EMAIL_PREFS,
  push: { mentioned: true, assigned: true, comment: true, due: true, invited: true, automation: true, reminder: true, handover: true, form: true },
  emailMode: 'bundle',
  quiet: { enabled: true, from: '20:00', to: '07:00', weekend: false },
  timeZone: null,
};

/** Wartezeit, bevor eine ungelesene Benachrichtigung per E-Mail rausgeht (in der App gelesen = keine Mail) */
export const EMAIL_DELAY_MIN: Record<EmailMode, number> = { instant: 2, bundle: 15, daily: 0 };

export function notifyPrefsOf(raw: unknown): NotifyPrefs {
  const p = (raw ?? {}) as Partial<NotifyPrefs> & { email?: Partial<EmailPrefs>; push?: Partial<EmailPrefs>; quiet?: Partial<QuietHours> };
  return {
    email: { ...DEFAULT_NOTIFY_PREFS.email, ...(p.email ?? {}) },
    push: { ...DEFAULT_NOTIFY_PREFS.push, ...(p.push ?? {}) },
    emailMode: p.emailMode && p.emailMode in EMAIL_MODE_LABEL ? p.emailMode : DEFAULT_NOTIFY_PREFS.emailMode,
    quiet: { ...DEFAULT_NOTIFY_PREFS.quiet, ...(p.quiet ?? {}) },
    timeZone: typeof p.timeZone === 'string' && validTimeZone(p.timeZone) ? p.timeZone : null,
  };
}

export function validTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Uhrzeit (Minuten seit Mitternacht) und Wochentag (0 = So) in einer Zeitzone */
export function localClock(date: Date, timeZone: string): { minutes: number; weekday: number; date: string } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return {
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
    weekday: wd,
    date: `${get('year')}-${get('month')}-${get('day')}`,
  };
}

const hm = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Liegt der Zeitpunkt in den Ruhezeiten der Person? (über Mitternacht möglich, z. B. 20:00–07:00) */
export function inQuietHours(prefs: Pick<NotifyPrefs, 'quiet' | 'timeZone'>, now: Date, fallbackTz: string): boolean {
  const q = prefs.quiet;
  if (!q.enabled && !q.weekend) return false;
  const { minutes, weekday } = localClock(now, prefs.timeZone ?? fallbackTz);
  if (q.weekend && (weekday === 0 || weekday === 6)) return true;
  if (!q.enabled) return false;
  const from = hm(q.from);
  const to = hm(q.to);
  if (from === to) return false;
  return from < to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

/** Reiter der Kartendetails (Teil der Adresse: `?card=…&cardTab=…`) */
export const CARD_TABS = ['uebersicht', 'details', 'dateien', 'kommentare', 'verlauf'] as const;
export type CardTab = (typeof CARD_TABS)[number];

/** Adresswert → Reiter; ältere Links mit „aktivitaet“ (bis 0.16) öffnen die Kommentare */
export function parseCardTab(v: string | null | undefined): CardTab {
  if (v === 'aktivitaet') return 'kommentare';
  return (CARD_TABS as readonly string[]).includes(v ?? '') ? (v as CardTab) : 'uebersicht';
}

/** Pfad zu Board, Karte und ggf. einem Kommentar (öffnet dann den Reiter „Kommentare“) */
export function cardPath(boardId: string, cardId?: string | null, commentId?: string | null): string {
  if (!cardId) return `/b/${boardId}`;
  return `/b/${boardId}?card=${cardId}${commentId ? `&cardTab=kommentare&comment=${commentId}` : ''}`;
}
